import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Buildings, FloppyDisk, ArrowLeft, Check, CloudArrowDown, PencilSimple, Trash } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

export default function ClientEnrollment() {
  const [clients, setClients] = useState([]);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const navigate = useNavigate();

  const loadClients = () => {
    api.get("/clients").then((r) => {
      setClients(r.data);
      const initial = {};
      r.data.forEach((c) => {
        initial[c.id] = {
          enrollment: c.enrollment || 0,
          nslp: c.nslp || 0,
          discount_rate: c.discount_rate || 0,
          c2_budget_estimate: c.c2_budget_estimate || 0,
        };
      });
      setEdits(initial);
    }).catch(() => toast.error("Failed to load clients"));
  };

  useEffect(() => { loadClients(); }, []);

  const setField = (clientId, field, value) => {
    setEdits((prev) => ({
      ...prev,
      [clientId]: { ...prev[clientId], [field]: value },
    }));
    setSaved(false);
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const updates = Object.entries(edits).map(([id, data]) => ({ id, ...data }));
      const { data } = await api.post("/clients/bulk-update-data", { updates });
      toast.success(`Updated ${data.updated} of ${data.total} clients`);
      setSaved(true);
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    return clients.some((c) => {
      const e = edits[c.id];
      if (!e) return false;
      return (
        (e.enrollment || 0) !== (c.enrollment || 0) ||
        (e.nslp || 0) !== (c.nslp || 0) ||
        (e.discount_rate || 0) !== (c.discount_rate || 0) ||
        (e.c2_budget_estimate || 0) !== (c.c2_budget_estimate || 0)
      );
    });
  };

  const saveRow = async (clientId) => {
    const e = edits[clientId];
    if (!e) return;
    try {
      await api.post("/clients/bulk-update-data", { updates: [{ id: clientId, ...e }] });
      toast.success("Saved");
      loadClients();
    } catch { toast.error("Failed to save"); }
  };

  const deleteClient = async (clientId, name) => {
    if (!window.confirm(`Delete "${name}"? This removes the client entirely.`)) return;
    try {
      await api.delete(`/clients/${clientId}`);
      toast.success(`"${name}" deleted`);
      loadClients();
    } catch { toast.error("Failed to delete"); }
  };

  const syncFromUSAC = async () => {
    setSyncing(true);
    try {
      await api.post("/clients/sync-usac-enrollment");
      toast.success("USAC enrollment sync started — this may take a few minutes");
      // Poll sync status
      const poll = setInterval(async () => {
        try {
          const { data } = await api.get("/usac/sync/status");
          if (data.last_enrollment_sync?.completed_at) {
            clearInterval(poll);
            setSyncing(false);
            toast.success(`Updated ${data.last_enrollment_sync.updated} of ${data.last_enrollment_sync.total} clients`);
            loadClients();
          }
        } catch { /* ignore */ }
      }, 5000);
      // Timeout after 5 minutes
      setTimeout(() => { clearInterval(poll); setSyncing(false); }, 300000);
    } catch {
      toast.error("Failed to sync from USAC");
      setSyncing(false);
    }
  };

  return (
    <div className="p-6" data-testid="client-enrollment-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <button data-testid="back-to-clients" onClick={() => navigate("/clients")} className="flex items-center gap-1 text-sm text-[#6B7280] hover:text-[#002FA7] mb-2 transition-colors">
            <ArrowLeft size={14} /> Back to Clients
          </button>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Enrollment & NSLP Data
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">Edit enrollment, NSLP, discount, and C2 budget for all clients inline</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="sync-usac-btn"
            onClick={syncFromUSAC}
            disabled={syncing}
            className="flex items-center gap-2 rounded-none font-medium px-4 py-2.5 text-sm bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] disabled:opacity-50 transition-colors"
          >
            <CloudArrowDown size={16} weight="bold" className={syncing ? "animate-pulse" : ""} />
            {syncing ? "Syncing USAC..." : "Sync from USAC"}
          </button>
          <button
            data-testid="save-all-btn"
            onClick={saveAll}
            disabled={saving}
            className={`flex items-center gap-2 rounded-none font-medium px-5 py-2.5 text-sm transition-colors ${
              saved ? "bg-[#00B341] text-white" : "bg-[#002FA7] text-white hover:bg-[#002480]"
            } disabled:opacity-50`}
          >
            {saved ? <><Check size={16} weight="bold" /> Saved</> : <><FloppyDisk size={16} weight="bold" /> {saving ? "Saving..." : "Save All"}</>}
          </button>
        </div>
      </div>

      {clients.length === 0 ? (
        <div className="bg-white border border-gray-200 p-12 text-center">
          <Buildings size={32} className="mx-auto text-[#6B7280] mb-3" />
          <p className="text-sm text-[#6B7280]">No clients found. Add clients first.</p>
        </div>
      ) : (
        <div className="space-y-0">
          {/* Header */}
          <div className="grid grid-cols-[1fr_100px_200px_100px_100px_150px_80px] gap-0 bg-[#F8F9FA] border border-gray-200 border-b-0">
            <div className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6B7280]">School / Entity</div>
            <div className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6B7280]">BEN</div>
            <div className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] font-semibold text-[#002FA7] border-l-2 border-l-[#002FA7]">Enrollment</div>
            <div className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] font-semibold text-[#002FA7]">NSLP %</div>
            <div className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] font-semibold text-[#002FA7]">Discount %</div>
            <div className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] font-semibold text-[#002FA7]">Est. C2 Budget</div>
            <div className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6B7280]">Actions</div>
          </div>

          {/* Rows */}
          {clients.map((client, idx) => {
            const e = edits[client.id] || {};
            const changed =
              (e.enrollment || 0) !== (client.enrollment || 0) ||
              (e.nslp || 0) !== (client.nslp || 0) ||
              (e.discount_rate || 0) !== (client.discount_rate || 0) ||
              (e.c2_budget_estimate || 0) !== (client.c2_budget_estimate || 0);

            return (
              <div
                key={client.id}
                data-testid={`enrollment-row-${client.id}`}
                className={`grid grid-cols-[1fr_100px_200px_100px_100px_150px_80px] gap-0 border border-gray-200 border-t-0 ${
                  changed ? "bg-[#002FA7]/5" : idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                }`}
              >
                {/* Name */}
                <div className="py-3 px-4 flex items-center gap-2">
                  <Buildings size={14} className="text-[#002FA7] flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#1A1D20] truncate">{client.name}</p>
                    <p className="text-[10px] text-[#6B7280]">{client.entity_type} {client.city && client.state ? `| ${client.city}, ${client.state}` : ""}</p>
                  </div>
                  {changed && <Badge className="rounded-none text-[8px] bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20 border ml-1">edited</Badge>}
                </div>
                {/* BEN */}
                <div className="py-3 px-4 flex items-center">
                  <span className="font-mono text-xs text-[#1A1D20]">{client.ben || "—"}</span>
                </div>
                {/* Enrollment */}
                <div className="py-2 px-3 flex items-center border-l-2 border-l-[#002FA7]/20">
                  <Input
                    data-testid={`enrollment-input-${client.id}`}
                    type="text"
                    value={e.enrollment ? Number(e.enrollment).toLocaleString() : ""}
                    onChange={(ev) => setField(client.id, "enrollment", parseInt(ev.target.value.replace(/,/g, "")) || 0)}
                    placeholder="0"
                    className="rounded-none h-8 text-sm"
                  />
                </div>
                {/* NSLP */}
                <div className="py-2 px-3 flex items-center">
                  <Input
                    data-testid={`nslp-input-${client.id}`}
                    type="number"
                    value={e.nslp || ""}
                    onChange={(ev) => setField(client.id, "nslp", parseFloat(ev.target.value) || 0)}
                    placeholder="0"
                    className="rounded-none h-8 text-sm"
                  />
                </div>
                {/* Discount */}
                <div className="py-2 px-3 flex items-center">
                  <Input
                    data-testid={`discount-input-${client.id}`}
                    type="number"
                    value={e.discount_rate || ""}
                    onChange={(ev) => setField(client.id, "discount_rate", parseFloat(ev.target.value) || 0)}
                    placeholder="0"
                    className="rounded-none h-8 text-sm"
                  />
                </div>
                {/* C2 Budget */}
                <div className="py-2 px-3 flex items-center gap-1">
                  <span className="text-sm text-[#6B7280]">$</span>
                  <Input
                    data-testid={`c2budget-input-${client.id}`}
                    type="text"
                    value={e.c2_budget_estimate ? Number(e.c2_budget_estimate).toLocaleString() : ""}
                    onChange={(ev) => setField(client.id, "c2_budget_estimate", parseFloat(ev.target.value.replace(/,/g, "")) || 0)}
                    placeholder="0"
                    className="rounded-none h-8 text-sm"
                  />
                </div>
                {/* Actions */}
                <div className="py-2 px-3 flex items-center gap-1">
                  <button data-testid={`save-row-${client.id}`} onClick={() => saveRow(client.id)} className="p-1.5 hover:bg-blue-50 transition-colors" title="Save this row">
                    <FloppyDisk size={14} className={changed ? "text-[#002FA7]" : "text-[#6B7280]"} />
                  </button>
                  <button data-testid={`delete-row-${client.id}`} onClick={() => deleteClient(client.id, client.name)} className="p-1.5 hover:bg-red-50 transition-colors" title="Delete client">
                    <Trash size={14} className="text-[#FF2A00]" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Sticky save bar when changes exist */}
      {hasChanges() && !saved && (
        <div className="fixed bottom-0 left-[240px] right-0 bg-[#1A1D20] text-white px-6 py-3 flex items-center justify-between z-50 border-t border-gray-700" data-testid="unsaved-bar">
          <span className="text-sm">You have unsaved changes</span>
          <button data-testid="save-bar-btn" onClick={saveAll} disabled={saving} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-1.5 text-sm flex items-center gap-2 disabled:opacity-50">
            <FloppyDisk size={14} /> {saving ? "Saving..." : "Save All Changes"}
          </button>
        </div>
      )}
    </div>
  );
}
